import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { ago, inr, num } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Empty, Pill, useAsync } from '../components/ui.jsx';

const KINDS = {
  rate_below_floor: 'Rate below the floor',
  credit_limit: 'Beyond the credit limit',
  credit_hold: 'Customer on credit hold',
  credit_overdue: 'Overdue bills',
};

/** Someone has to take responsibility for a discount or a credit stretch. This is where. */
export default function Approvals() {
  const session = useSession();
  const [status, setStatus] = useState('pending');
  const list = useAsync(() => api.get(`/api/approvals?status=${status}`), [status]);
  const [error, setError] = useState(null);
  const [note, setNote] = useState({});
  const canDecide = ['tenant_admin', 'accounts'].includes(session.user.role);

  const decide = async (id, decision) => {
    setError(null);
    try {
      await api.post(`/api/approvals/${id}/decide`, { decision, note: note[id] || undefined });
      list.reload();
    } catch (e) { setError(e.message); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Approvals</h1>
          <div className="page-sub">
            Orders held because a rate is below its floor or a buyer is past their limit. Approving one puts a name
            against the decision, and the order carries that record for good.
          </div>
        </div>
        <div className="segmented" role="group" aria-label="View">
          {[['pending', 'Waiting'], ['approved,rejected', 'Decided'], ['', 'All']].map(([key, label]) => (
            <button key={label} aria-pressed={status === key} onClick={() => setStatus(key)}>{label}</button>
          ))}
        </div>
      </div>

      <Alert onRetry={() => setError(null)}>{error}</Alert>
      <Alert onRetry={list.reload}>{list.error}</Alert>
      {list.loading && !list.data && <CardSkeleton lines={3} />}

      {list.data && list.data.approvals.length === 0 && (
        <Card><Empty title="Nothing waiting">Every order has gone through on its own terms.</Empty></Card>
      )}

      {list.data && list.data.approvals.map((a) => {
        // The server refuses a decision on one's own request; don't offer it.
        const own = a.requested_by === session.user.id;
        return (
        <Card key={a.id}
          title={a.title}
          note={`${KINDS[a.kind] || a.kind} · requested by ${a.requested_by_name || 'unknown'} ${ago(a.requested_at)}`}
          actions={<Pill value={a.status === 'pending' ? 'pending' : a.status === 'approved' ? 'issued' : 'cancelled'}
            label={a.status} />}>
          {a.detail && <div className="card-note" style={{ marginBottom: 10 }}>{a.detail}</div>}

          {a.kind === 'rate_below_floor' && a.data && (
            <div className="row">
              <div><div className="label">Item</div>{a.data.item_name}</div>
              <div><div className="label">Asked</div><span className="num">{inr(a.data.net_rate)}</span></div>
              <div><div className="label">Floor</div><span className="num">{inr(a.data.min_rate)}</span></div>
              <div><div className="label">Short by</div>
                <span className="num" style={{ color: 'var(--critical-text)' }}>{inr(a.data.shortfall)}</span></div>
            </div>
          )}

          {(a.kind === 'credit_limit' || a.kind === 'credit_hold') && a.data && (
            <div className="row">
              <div><div className="label">Outstanding</div><span className="num">{inr(a.data.outstanding, 0)}</span></div>
              <div><div className="label">This order</div><span className="num">{inr(a.data.order_value, 0)}</span></div>
              <div><div className="label">Limit</div><span className="num">{inr(a.data.credit_limit, 0)}</span></div>
              <div><div className="label">Over by</div>
                <span className="num" style={{ color: 'var(--critical-text)' }}>
                  {inr(Math.abs(num(a.data.headroom)), 0)}
                </span></div>
            </div>
          )}

          {a.kind === 'credit_overdue' && a.data && (
            <>
              <div className="row">
                <div><div className="label">Overdue</div>
                  <span className="num" style={{ color: 'var(--critical-text)' }}>{inr(a.data.amount, 0)}</span></div>
                {a.data.oldest_days !== null && a.data.oldest_days !== undefined && (
                  <div><div className="label">Oldest</div><span className="num">{a.data.oldest_days} days past due</span></div>
                )}
                {a.data.grace_days !== undefined && (
                  <div><div className="label">Grace allowed</div><span className="num">{a.data.grace_days} days</span></div>
                )}
              </div>
              {(a.data.bills || []).length > 0 && (
                <div className="card-note" style={{ marginTop: 8 }}>
                  {a.data.bills.slice(0, 6).map((b) => `${b.bill_ref} (${inr(b.amount, 0)}, ${b.days_overdue} days)`).join(' · ')}
                </div>
              )}
            </>
          )}

          {a.do_number && (
            <div className="card-note">
              Order <Link to="/delivery-orders">{a.do_number}</Link> for {a.buyer_name}
            </div>
          )}

          {a.status === 'pending' && canDecide && !own && (
            <div className="inline" style={{ marginTop: 14 }}>
              <input placeholder="Why (goes on the record)" value={note[a.id] || ''} style={{ maxWidth: 380 }}
                onChange={(e) => setNote({ ...note, [a.id]: e.target.value })} />
              <button className="btn" onClick={() => decide(a.id, 'approved')}>Approve</button>
              <button className="btn secondary" onClick={() => decide(a.id, 'rejected')}>Refuse</button>
            </div>
          )}
          {a.status === 'pending' && (!canDecide || own) && (
            <div className="card-note" style={{ marginTop: 10 }}>
              {own
                ? 'Waiting for someone else at the accounts desk. You cannot approve your own request.'
                : 'Waiting for the accounts desk.'}
            </div>
          )}
          {a.status !== 'pending' && (
            <div className="card-note" style={{ marginTop: 10 }}>
              {a.status === 'approved' ? 'Approved' : 'Refused'} by {a.decided_by_name || 'unknown'} {ago(a.decided_at)}
              {a.decision_note ? ` — “${a.decision_note}”` : ''}
            </div>
          )}
        </Card>
        );
      })}
    </>
  );
}
